import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom"; // Added useLocation

import { LABELS } from "~/resources/Labels";

import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SliderField } from "../../components/commons/slider/SIMPLSlider";

import { UseVmTemplateForm } from "./UseVmTemplateForm";

import {
  extractCloudProviderList,
  extractComponentsDetails,
  extractEnvDetails,
  getProviders,
} from "~/Utils/Utils";

import { useGetCloudProvisionerFullQuery } from "~/services/cloudProvisioner";
import { CloudProvisionerResponse, TemplateResponse } from "~/models/Commons ";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { useGetVmTemplatesQuery } from "~/services/vm/VmTemplate";
import { useTranslation } from "react-i18next";

// -------------------- EditVmTemplate Component --------------------
export const EditVmTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get editedItem from navigation state
  const editedItem = location.state?.editedItem as TemplateResponse | null;
  const { refetch } = useGetVmTemplatesQuery({
    page: 0,
    size: 999,
    active: false,
  });
  const {
    handleSubmit,
    updateForm,
    control,
    errors,
    selectCloudEnvData,
    cloudProviderName,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    providerData,
    provisionerData,
    watch,
    resetField,
    setValue,
    isLoading,
    isError,
  } = UseVmTemplateForm(refetch, editedItem?.id!);

  // -------------------- State --------------------
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const [cpu, setCpu] = useState<{ min: number; max: number } | null>(null);
  const [ram, setRam] = useState<{ min: number; max: number } | null>(null);
  const [showSubmit, setShowSubmit] = useState<boolean>(false);
  const [storage, setStorage] = useState<{ min: number; max: number } | null>(
    null,
  );
  const [cloudProvisionerTemplateId, setCloudProvisionerTemplateId] = useState<
    number | undefined
  >(undefined);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const [selectClouldProvisioner, setSelectClouldProvisioner] = useState<any[]>(
    [],
  );
  const [clouldProvisionerList, setClouldProvisionerList] = useState<any[]>([]);
  const [selectedCloudEnvironments, setSelectedCloudEnvironments] = useState<
    any[]
  >([]);
  const [selectCloudEnv, setSelectCloudEnv] = useState<any[]>([]);
  const [selectProvider, setSelectProvider] = useState<any[]>([]);
  const [selectVmConfiguration, setSelectVmConfiguration] = useState<any[]>([]);
  const [selectPostProvisioning, setSelectPostProvisioning] = useState<any[]>(
    [],
  );
  const [selectPolicy, setSelectPolicy] = useState<any[]>([]);
  const [showOvgInput, setShowOvgInput] = useState<boolean>(false);

  const { data } = useGetCloudProvisionerFullQuery();
  const { t } = useTranslation();

  // -------------------- Handlers --------------------
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(updateForm)(event);
  };

  const getVmComponentsOptions = (
    components: { name: string; id: number }[],
  ) => {
    return components.map((cp) => ({
      label: cp.name,
      value: cp.id,
    }));
  };

  const getCloudProviderOptions = (
    provider: { cloudProviderName: string; id: number }[],
  ) =>
    provider?.map((cloudEnv) => ({
      label: cloudEnv.cloudProviderName,
      value: cloudEnv.id,
    }));

  const handleCloudProviderChange = (
    value: number,
    onChange: (val: number) => void,
  ) => {
    onChange(value);
    const selected = cloudProviderName?.find((p) => p.id === value);
    const isOvh = selected?.cloudProviderName === LABELS.Ovh;
    setShowOvgInput(isOvh);

    if (isOvh) {
      resetField("os");
      resetField("cpuCore");
      resetField("ram");
      resetField("storage");
    } else {
      resetField("os");
    }
  };

  const fetchProviderList = () => {
    setCloudProviderList(getProviders(providerData));
  };

  // -------------------- Init Template --------------------
  const initVmTemplate = () => {
    const payloadData = data?.payload as any | undefined;
    const provisionerPayload = provisionerData?.payload as
      CloudProvisionerResponse | undefined;
    const envPayload = selectCloudEnvData?.payload as any | undefined;
    const configPayload = ConfigurationComponentsData?.payload as
      any | undefined;
    const postProvisionPayload = ProvissioningComponentsData?.payload as
      any | undefined;
    const policyPayload = PolicyComponentsData?.payload as any | undefined;

    if (payloadData?.content) {
      setClouldProvisionerList(payloadData?.content);
      setSelectClouldProvisioner(
        payloadData.content.map(({ id, fileName }: any) => ({ id, fileName })),
      );
    }

    if (envPayload) setSelectCloudEnv(extractEnvDetails(envPayload.content));
    const optionProvider = extractCloudProviderList(envPayload?.content);
    setSelectedCloudEnvironments(envPayload?.content);
    setSelectProvider(optionProvider);

    if (configPayload)
      setSelectVmConfiguration(extractComponentsDetails(configPayload.content));

    if (postProvisionPayload)
      setSelectPostProvisioning(
        extractComponentsDetails(postProvisionPayload.content),
      );

    if (policyPayload)
      setSelectPolicy(extractComponentsDetails(policyPayload.content));

    if (provisionerPayload) {
      const {
        minCpuCore,
        maxCpuCore,
        minRam,
        maxRam,
        minStorage,
        maxStorage,
        os,
      } = provisionerPayload;
      setCpu({ min: minCpuCore, max: maxCpuCore });
      setRam({ min: minRam, max: maxRam });
      setStorage({ min: minStorage, max: maxStorage });
      setOsOptions(os);
    }
  };

  // -------------------- Effects --------------------
  useEffect(() => {
    initVmTemplate();
    fetchProviderList();
  }, [
    data,
    provisionerData,
    selectCloudEnv,
    ConfigurationComponentsData,
    PolicyComponentsData,
    providerData,
  ]);

  const providerId: any = watch("providerType");
  useEffect(() => {
    fetchProviderList();
    if (providerId) setShowOvgInput(providerId.label === "Ovh");
  }, [providerId]);

  const provider = watch("providerType");
  useEffect(() => {
    if (!provider) return;
    setShowOvgInput(provider.label === LABELS.Ovh);
  }, [provider, setValue]);

  const filteredEnvironments = provider
    ? (selectedCloudEnvironments?.filter(
        (env) => env.cloudProvider?.cloudProviderName === provider.label,
      ) ?? [])
    : selectCloudEnv;

  const filteredCloudProvisionerTemplate = provider
    ? (clouldProvisionerList?.filter((env) =>
        provider.label === LABELS.Ovh ? env.isOvh : !env.isOvh,
      ) ?? [])
    : selectClouldProvisioner;

  const watchProvisioner = watch("cloudProvisionerTemplateId");
  useEffect(() => {
    setCloudProvisionerTemplateId(watchProvisioner?.value || undefined);
  }, [watchProvisioner, provisionerData]);

  // -------------------- Prefill edited item --------------------
  useEffect(() => {
    if (
      !editedItem ||
      !selectCloudEnv.length ||
      !selectProvider.length ||
      !selectClouldProvisioner.length
    )
      return;

    setValue("name", editedItem.name || "");
    setValue("description", editedItem.description || "");

    const env = selectCloudEnv.find(
      (e) => e.id === editedItem.cloudEnvironment.id,
    );
    if (env)
      setValue("cloudEnvironmentId", {
        label: env.environmentName,
        value: env.id,
      });
    const provider = cloudProviderList?.find(
      (p) => p.id === editedItem.cloudEnvironment.cloudProvider.id,
    );
    if (provider)
      setValue("providerType", {
        label: provider.cloudProviderName,
        value: provider.id,
      });

    const provisioner = selectClouldProvisioner.find(
      (p) => p.id === editedItem.cloudProvisionerTemplate.id,
    );
    if (provisioner)
      setValue("cloudProvisionerTemplateId", {
        label: provisioner.fileName,
        value: provisioner.id,
      });

    if (Array.isArray(editedItem.components)) {
      editedItem.components.forEach((component) => {
        const vmConf = selectVmConfiguration.find((c) => c.id === component.id);
        if (vmConf) {
          setValue("vmconfiguration", { label: vmConf.name, value: vmConf.id });
        }

        const postProv = selectPostProvisioning.find(
          (p) => p.id === component.id,
        );
        if (postProv) {
          setValue("vmPostProviosiing", {
            label: postProv.name,
            value: postProv.id,
          });
        }

        const policy = selectPolicy.find((p) => p.id === component.id);
        if (policy) {
          setValue("vmPolicy", { label: policy.name, value: policy.id });
        }
      });
    } else {
      const component = editedItem.components;

      const vmConf = selectVmConfiguration.find((c) => c.id === component.id);
      if (vmConf) {
        setValue("vmconfiguration", { label: vmConf.name, value: vmConf.id });
      }

      const postProv = selectPostProvisioning.find(
        (p) => p.id === component.id,
      );
      if (postProv) {
        setValue("vmPostProviosiing", {
          label: postProv.name,
          value: postProv.id,
        });
      }

      const policy = selectPolicy.find((p) => p.id === component.id);
      if (policy) {
        setValue("vmPolicy", { label: policy.name, value: policy.id });
      }
    }

    if (editedItem.os)
      setValue("os", { label: editedItem.os, value: editedItem.os });
    setValue("cpuCore", editedItem.cpuCore ?? 0);
    setValue("ram", editedItem.ram ?? 0);
    setValue("storage", editedItem.storage ?? 0);
  }, [
    editedItem,
    selectCloudEnv,
    cloudProviderList,
    selectClouldProvisioner,
    selectVmConfiguration,
    selectPostProvisioning,
    selectPolicy,
    setValue,
  ]);

  const handleCancel = () => {
    navigate("/vm-template");
  };

  // -------------------- Render (with 2-3-3-4 layout) --------------------
  return (
    <SIMPLMenuHeader>
      {/* PAGE CONTAINER */}
      <LoadingWrapper
        loadingTitle="Loading VmTemplate..."
        isLoading={isLoading}
        isError={isError}
        testId="vmTemplate-edit-loading"
      >
        <div className="flex justify-center bg-white px-6 py-8">
          {/* CARD */}
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            {/* HEADER: BREADCRUMB + TITLE */}
            <div className="px-8 pt-6">
              <SIMPLBreadcrumb />

              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {t("VmTemplate")}
              </h1>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="space-y-6 px-8 pb-8 pt-6">
                {/* NAME */}
                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    id="name"
                    name="name"
                    control={control}
                    label={t("Name")}
                    placeholder={t("Name")}
                    readOnly
                    required
                    fontWeight="bold"
                    background="#F5F5F5"
                    rules={{
                      required: { value: true, message: t("NameRequired") },
                    }}
                    errorLabel={errors.name?.message}
                    testId="vmTemplate-edit-name"
                  />
                </div>

                {/* DESCRIPTION */}
                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    id="description"
                    name="description"
                    control={control}
                    label={t("Description")}
                    placeholder={t("Description")}
                    readOnly
                    fontWeight="bold"
                    background="#F5F5F5"
                    errorLabel={errors.description?.message}
                    testId="vmTemplate-edit-description"
                  />
                </div>
                {/* OUTPUT TEMPLATE SELECT */}
                <Controller
                  name="providerType"
                  control={control}
                  render={({ field }) => (
                    <SIMPLSelectDropdown
                      id=""
                      label={t("OutputTemplate")}
                      options={getCloudProviderOptions(cloudProviderList)}
                      value={field.value}
                      onChange={field.onChange}
                      hideErrorLabel={true}
                      placeholder={t("OutputTemplate")}
                      readOnly
                      isRequired={false}
                      background="white"
                      testId="output-template"
                    />
                  )}
                />
                {/* CLOUD PROVIDER */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="providerType"
                    control={control}
                    rules={{ required: t("MessageCloudProviderRequired") }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="providerId"
                        label={t("Provider")}
                        placeholder={t("Provider")}
                        options={getCloudProviderOptions(cloudProviderList)}
                        isRequired
                        isDisabled
                        value={field.value}
                        onChange={(val: any) =>
                          handleCloudProviderChange(val, field.onChange)
                        }
                        hideErrorLabel
                        errorLabel={errors.providerType?.message as string}
                        testId="vmTemplate-edit-providerType"
                      />
                    )}
                  />
                </div>
                {/* CLOUD ENVIRONMENT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudEnvironmentId"
                    control={control}
                    rules={{
                      required: t("MessageCloudEnvironmentRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudEnvironmentId"
                        label={t("CloudEnvironment")}
                        placeholder={t("CloudEnvironment")}
                        options={filteredEnvironments.map((cloudEnv) => ({
                          label: cloudEnv.environmentName,
                          value: cloudEnv.id,
                        }))}
                        isRequired
                        isDisabled
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel
                        errorLabel={
                          errors.cloudEnvironmentId?.message as string
                        }
                        testId="vmTemplate-edit-cloudenvironmentId"
                      />
                    )}
                  />
                </div>

                {/* PROVISIONING TEMPLATE */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudProvisionerTemplateId"
                    control={control}
                    rules={{
                      required: t("MessageProvisioningTemplateRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudProvisionerTemplateId"
                        label={t("ProvisioningTemplate")}
                        placeholder={t("ProvisioningTemplate")}
                        options={filteredCloudProvisionerTemplate.map(
                          (template) => ({
                            label: template.fileName,
                            value: template.id,
                          }),
                        )}
                        isRequired
                        isDisabled
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel
                        errorLabel={
                          errors.cloudProvisionerTemplateId?.message as string
                        }
                        testId="vmTemplate-edit-cloudProvisionerTemplateId"
                      />
                    )}
                  />
                </div>

                {/* VM COMPONENTS */}
                <div className="grid gap-6 md:grid-cols-2">
                  {/* OS */}
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: {
                        value: !showOvgInput,
                        message: t("MessageOsRequired"),
                      },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={t("Os")}
                        placeholder={t("SelectOs")}
                        options={osOptions?.map((os) => ({
                          label: os,
                          value: os,
                        }))}
                        value={field.value}
                        isDisabled
                        onChange={field.onChange}
                        hideErrorLabel
                        isRequired={!showOvgInput}
                        errorLabel={errors.os?.message as string}
                        testId="vmTemplate-edit-os"
                      />
                    )}
                  />

                  {/* VM CONFIGURATION */}
                  <Controller
                    name="vmconfiguration"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value ||
                        watch("vmPostProviosiing") ||
                        watch("vmPolicy") ||
                        t("MessageVmConfigurationRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmconfiguration"
                        label={t("VmConfiguration")}
                        placeholder={t("VmConfiguration")}
                        options={getVmComponentsOptions(selectVmConfiguration)}
                        value={field.value || null}
                        onChange={(selected: any) => {
                          field.onChange(selected ?? null);
                          setShowSubmit(true);
                        }}
                        hideErrorLabel
                        errorLabel={errors.vmconfiguration?.message as string}
                        testId="vmTemplate-edit-vm-configuration"
                      />
                    )}
                  />

                  {/* POST PROVISIONING */}
                  <Controller
                    name="vmPostProviosiing"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value ||
                        watch("vmconfiguration") ||
                        watch("vmPolicy") ||
                        t("MessageVmPostProvisioningRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPostProviosiing"
                        label={t("VmPostProviSioning")}
                        placeholder={t("VmPostProviSioning")}
                        options={getVmComponentsOptions(selectPostProvisioning)}
                        value={field.value || null}
                        onChange={(selected: any) => {
                          field.onChange(selected ?? null);
                          setShowSubmit(true);
                        }}
                        hideErrorLabel
                        errorLabel={errors.vmPostProviosiing?.message as string}
                        testId="vmTemplate-edit-vm-postProviosiing"
                      />
                    )}
                  />

                  {/* POLICY */}
                  <Controller
                    name="vmPolicy"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value ||
                        watch("vmconfiguration") ||
                        watch("vmPostProviosiing") ||
                        t("MessageVmPolicyRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPolicy"
                        label={t("VmPolicy")}
                        placeholder={t("VmPolicy")}
                        options={getVmComponentsOptions(selectPolicy)}
                        value={field.value || null}
                        onChange={(selected: any) => {
                          field.onChange(selected ?? null);
                          setShowSubmit(true);
                        }}
                        hideErrorLabel
                        errorLabel={errors.vmPolicy?.message as string}
                        testId="vmTemplate-edit-vm-policy"
                      />
                    )}
                  />
                </div>

                {/* SLIDERS */}
                {!showOvgInput && (
                  <>
                    <div className="space-y-6 pt-2">
                      {/* CPU */}
                      <Controller
                        name="cpuCore"
                        control={control}
                        rules={{
                          required: {
                            value: !showOvgInput,
                            message: t("MessageCpuCoreRequired"),
                          },
                        }}
                        render={({ field }) => (
                          <SliderField
                            id="cpuCores"
                            label={t("CpuCore")}
                            value={field.value}
                            min={cpu?.min}
                            max={cpu?.max}
                            disabled
                            valueLabel={t("CpuCoresValue")}
                            onChange={field.onChange}
                            required={!showOvgInput}
                            errorLabel={errors.cpuCore?.message as string}
                            testId="vmTemplate-edit-cpuCores"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      {/* RAM */}
                      <Controller
                        name="ram"
                        control={control}
                        rules={{
                          required: {
                            value: !showOvgInput,
                            message: t("MessageRamRequired"),
                          },
                        }}
                        render={({ field }) => (
                          <SliderField
                            id="ram"
                            label={t("Ram")}
                            value={field.value}
                            min={ram?.min}
                            max={ram?.max}
                            disabled
                            valueLabel={t("RamValue")}
                            onChange={field.onChange}
                            required={!showOvgInput}
                            errorLabel={errors.ram?.message as string}
                            testId="vmTemplate-edit-ram"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      {/* STORAGE */}
                      <Controller
                        name="storage"
                        control={control}
                        rules={{
                          required: {
                            value: !showOvgInput,
                            message: t("MessageStorageRequired"),
                          },
                        }}
                        render={({ field }) => (
                          <SliderField
                            id="storage"
                            label={t("Storage")}
                            value={field.value}
                            min={storage?.min}
                            max={storage?.max}
                            disabled
                            valueLabel={t("StorageValue")}
                            onChange={field.onChange}
                            required={!showOvgInput}
                            errorLabel={errors.storage?.message as string}
                            testId="vmTemplate-edit-storage"
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex w-full justify-end gap-3 rounded-b-xl border-t border-[#D6D6D6] bg-[#F5F5F5] px-6 py-4">
                <SIMPLButtonCustomized
                  label={t("Cancel")}
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="vmTemplate-edit-button-cancel"
                />
                {showSubmit && (
                  <SIMPLButtonCustomized
                    label={t("Submit")}
                    primary
                    testId="vmTemplate-edit-button-submit"
                  />
                )}
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};
